import React from 'react';
import { ShieldCheck, BadgeCheck, Lock, Sparkles, IdCard } from 'lucide-react';

/**
 * TrustBadge — semantic trust indicator used across clinician cards,
 * profiles, AI outputs, and verification flows.
 *
 * variants: verified | background-checked | license | npi-verified | encrypted | ai-confidence
 */
export const TrustBadge = ({ variant = 'verified', label, score, className = '' }) => {
  const config = {
    verified: {
      icon: BadgeCheck,
      defaultLabel: 'Verified',
      bg: 'bg-cn-sage-100 dark:bg-cn-primary/25',
      text: 'text-cn-primary dark:text-cn-sage-200',
      ring: 'ring-1 ring-cn-primary/20',
    },
    'background-checked': {
      icon: ShieldCheck,
      defaultLabel: 'Background Checked',
      bg: 'bg-cn-sage-100 dark:bg-cn-primary/25',
      text: 'text-cn-primary dark:text-cn-sage-200',
      ring: 'ring-1 ring-cn-primary/20',
    },
    license: {
      icon: BadgeCheck,
      defaultLabel: 'License Active',
      bg: 'bg-emerald-50 dark:bg-emerald-900/30',
      text: 'text-emerald-800 dark:text-emerald-300',
      ring: 'ring-1 ring-emerald-700/15',
    },
    'npi-verified': {
      icon: IdCard,
      defaultLabel: 'NPI Verified',
      bg: 'bg-sky-50 dark:bg-sky-900/30',
      text: 'text-sky-800 dark:text-sky-300',
      ring: 'ring-1 ring-sky-700/15',
    },
    encrypted: {
      icon: Lock,
      defaultLabel: 'Encrypted',
      bg: 'bg-cn-bone-200 dark:bg-cn-ink-700',
      text: 'text-cn-text dark:text-cn-bone-100',
      ring: 'ring-1 ring-cn-border',
    },
    'ai-confidence': {
      icon: Sparkles,
      defaultLabel: score != null ? `AI Confidence ${score}%` : 'AI Suggested',
      bg: 'bg-cn-terracotta-50 dark:bg-cn-terracotta-700/30',
      text: 'text-cn-terracotta-700 dark:text-cn-terracotta-100',
      ring: 'ring-1 ring-cn-terracotta-200',
    },
  };

  const c = config[variant] || config.verified;
  const Icon = c.icon;

  return (
    <span
      className={`inline-flex items-center gap-1.5 ${c.bg} ${c.text} ${c.ring} px-2.5 py-1 rounded-full text-xs font-semibold ${className}`}
      data-testid={`trust-badge-${variant}`}
    >
      <Icon className="w-3.5 h-3.5" strokeWidth={2.2} />
      {label || c.defaultLabel}
    </span>
  );
};

export default TrustBadge;
